import React, { ReactElement } from "react"
import { StyleProp, TextStyle, TouchableOpacity, TouchableOpacityProps, View, ViewStyle } from "react-native"

import { LinearGradient } from "expo-linear-gradient"
import { Edge, SafeAreaView, SafeAreaViewProps, useSafeAreaInsets } from "react-native-safe-area-context"

import { isRTL, translate } from "../i18n"
import { colors, spacing } from "../theme"
import { sizes } from "../theme/sizes"
import { Icon, IconTypes } from "./Icon"
import { Text, TextProps } from "./Text"

export interface HeaderProps {
  /**
   * Children components.
   */
  children?: React.ReactNode
  /**
   * The layout of the title relative to the action components.
   * - `center` will force the title to always be centered relative to the header. If the title or the action buttons are too long, the title will be cut off.
   * - `flex` will attempt to center the title relative to the action buttons. If the action buttons are different widths, the title will be off-center relative to the header.
   */
  titleMode?: "center" | "flex"
  /**
   * Optional title style override.
   */
  titleStyle?: StyleProp<TextStyle>
  /**
   * Optional header style override.
   */
  containerStyle?: StyleProp<ViewStyle>
  /**
   * Colors to render the header with gradeint
   */
  gradientColors?: string[]
  /**
   * Title text to display if not using `tx` or nested components.
   */
  title?: TextProps["text"]
  /**
   * Title text which is looked up via i18n.
   */
  titleTx?: TextProps["tx"]
  /**
   * Optional options to pass to i18n. Useful for interpolation
   * as well as explicitly setting locale or translation fallbacks.
   */
  titleTxOptions?: TextProps["txOptions"]
  /**
   * Icon that should appear on the left.
   * Can be used with `onLeftPress`.
   */
  leftIcon?: IconTypes
  /**
   * An optional tint color for the left icon
   */
  leftIconColor?: string
  /**
   * Left action text to display if not using `leftTx`.
   * Can be used with `onLeftPress`. Overrides `leftIcon`.
   */
  leftText?: TextProps["text"]
  /**
   * Left action text text which is looked up via i18n.
   * Can be used with `onLeftPress`. Overrides `leftIcon`.
   */
  leftTx?: TextProps["tx"]
  /**
   * Left action custom ReactElement if the built in action props don't suffice.
   * Overrides `leftIcon`, `leftTx` and `leftText`.
   */
  LeftActionComponent?: ReactElement
  /**
   * Optional options to pass to i18n. Useful for interpolation
   * as well as explicitly setting locale or translation fallbacks.
   */
  leftTxOptions?: TextProps["txOptions"]
  /**
   * What size the title text will have
   */
  leftTextSize?: TextProps["size"]
  /**
   * What happens when you press the left icon or text action.
   */
  onLeftPress?: TouchableOpacityProps["onPress"]
  /**
   * Icon that should appear on the right.
   * Can be used with `onRightPress`.
   */
  rightIcon?: IconTypes
  /**
   * An optional tint color for the right icon
   */
  rightIconColor?: string
  /**
   * Right action text to display if not using `rightTx`.
   * Can be used with `onRightPress`. Overrides `rightIcon`.
   */
  rightText?: TextProps["text"]
  /**
   * Right action text text which is looked up via i18n.
   * Can be used with `onRightPress`. Overrides `rightIcon`.
   */
  rightTx?: TextProps["tx"]
  /**
   * Right action custom ReactElement if the built in action props don't suffice.
   * Overrides `rightIcon`, `rightTx` and `rightText`.
   */
  RightActionComponent?: ReactElement
  /**
   * Optional options to pass to i18n. Useful for interpolation
   * as well as explicitly setting locale or translation fallbacks.
   */
  rightTxOptions?: TextProps["txOptions"]
  /**
   * What size the title text will have
   */
  rightTextSize?: TextProps["size"]
  /**
   * What happens when you press the right icon or text action.
   */
  onRightPress?: TouchableOpacityProps["onPress"]
  /**
   * Override the default edges for the safe area.
   */
  safeAreaEdges?: Edge[]
  /**
   * Pass any additional props directly to the SafeAreaView component.
   */
  SafeAreaViewProps?: SafeAreaViewProps
}

interface HeaderActionProps {
  icon?: IconTypes
  iconColor?: string
  text?: TextProps["text"]
  tx?: TextProps["tx"]
  txOptions?: TextProps["txOptions"]
  onPress?: TouchableOpacityProps["onPress"]
  ActionComponent?: ReactElement
  size: TextProps["size"]
  textStyle?: StyleProp<TextStyle>
}

/**
 * Header that appears on many screens. Will hold navigation buttons and screen title.
 * The Header is meant to be used with the `screenOptions.header` option on navigators,routes, or screen components via `navigation.setOptions({ header })`.
 *
 * - [Documentation and Examples](https://github.com/infinitered/ignite/blob/master/docs/Components-Header.md)
 */
export function Header(props: HeaderProps) {
  const {
    gradientColors = [colors.palette.primary800, colors.palette.primary500],
    LeftActionComponent,
    leftIcon,
    leftIconColor,
    leftText,
    leftTx,
    leftTxOptions,
    leftTextSize = "xl",
    onLeftPress,
    onRightPress,
    RightActionComponent,
    rightIcon,
    rightIconColor,
    rightText,
    rightTx,
    rightTxOptions,
    rightTextSize = "md",
    safeAreaEdges = ["top"],
    SafeAreaViewProps,
    title,
    titleMode = "center",
    titleTx,
    titleTxOptions,
    titleStyle: $titleStyleOverride,
    containerStyle: $containerStyleOverride,
    children,
  } = props

  const insets = useSafeAreaInsets()

  const Wrapper = safeAreaEdges?.length ? SafeAreaView : View

  const titleContent = titleTx ? translate(titleTx, titleTxOptions) : title

  return (
    <Wrapper
      edges={safeAreaEdges}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...SafeAreaViewProps}
      style={[$safeArea, SafeAreaViewProps?.style]}
    >
      {gradientColors
        ? (
          <LinearGradient
            colors={gradientColors}
            end={[1.0, 0.5]}
            locations={[0.0, 1.0]}
            start={[0.0, 0.5]}
            style={[$gradientStatusBar, { height: insets.top }]}
          />
        )
        : null}
      <LinearGradient
        colors={gradientColors}
        end={[1.0, 0.5]}
        locations={[0.0, 1.0]}
        start={[0.0, 0.5]}
        style={[$container, $containerStyleOverride]}
      >
        <HeaderAction
          ActionComponent={LeftActionComponent}
          icon={leftIcon}
          iconColor={leftIconColor}
          size={leftTextSize}
          text={leftText}
          tx={leftTx}
          txOptions={leftTxOptions}
          onPress={onLeftPress}
        />

        {!!titleContent && (
          <Text
            size="md"
            style={[titleMode === "center" && $centerTitle, titleMode === "flex" && $flexTitle, $titleStyleOverride]}
            text={titleContent}
            weight="medium"
          />
        )}

        {children}

        <HeaderAction
          ActionComponent={RightActionComponent}
          icon={rightIcon}
          iconColor={rightIconColor}
          size={rightTextSize}
          text={rightText}
          textStyle={$actionTextShadow}
          tx={rightTx}
          txOptions={rightTxOptions}
          onPress={onRightPress}
        />
      </LinearGradient>
    </Wrapper>
  )
}

function HeaderAction(props: HeaderActionProps) {
  const { icon, text, tx, txOptions, onPress, ActionComponent, iconColor, size, textStyle } = props

  const content = tx ? translate(tx, txOptions) : text

  if (ActionComponent) {
    return ActionComponent
  }

  if (content) {
    return (
      <TouchableOpacity
        activeOpacity={0.8}
        disabled={!onPress}
        style={$actionTextContainer}
        onPress={onPress}
      >
        <Text
          size={size}
          style={[$actionText, textStyle]}
          text={content}
          weight="normal"
        />
      </TouchableOpacity>
    )
  }

  if (icon) {
    return (
      <Icon
        color={iconColor}
        containerStyle={$actionIconContainer}
        icon={icon}
        size={24}
        style={isRTL ? { transform: [{ rotate: "180deg" }] } : {}}
        onPress={onPress}
      />
    )
  }

  return <View style={$actionFillerContainer} />
}

const $safeArea: ViewStyle = {
  width: "100%",
  shadowColor: "#000",
  shadowOffset: {
    width: 0,
    height: 10,
  },
  shadowOpacity: 0.2,
  shadowRadius: 10,
  elevation: 5,
}

const $container: ViewStyle = {
  height: sizes.header.height,
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
}

const $centerTitle: TextStyle = {
  position: "absolute",
  width: "100%",
  textAlign: "center",
  paddingHorizontal: spacing.xxl,
  zIndex: 1,
}

const $flexTitle: TextStyle = {
  flex: 1,
  textAlign: "center",
}

const $actionTextContainer: ViewStyle = {
  flexGrow: 0,
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  paddingHorizontal: spacing.xl,
  zIndex: 2,
}

const $actionText: TextStyle = {
  color: colors.palette.neutral100,
}

const $actionTextShadow: TextStyle = {
  textShadowColor: colors.palette.neutral600,
  textShadowOffset: { width: 0, height: 1 },
  textShadowRadius: 1,
}

const $actionIconContainer: ViewStyle = {
  flexGrow: 0,
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  paddingHorizontal: spacing.xl,
  zIndex: 2,
}

const $actionFillerContainer: ViewStyle = {
  width: 16,
}

const $gradientStatusBar: ViewStyle = {
  position: "absolute",
  width: "100%",
  height: 10,
  top: 0,
}
